import { z } from 'zod';
import {
  CapacityView,
  CostAttributionView,
  FundingView,
  QueryMetadata,
  ScenarioCatalogueView,
  ScenarioComparisonView,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * Portfolio finance and planning, over HTTP.
 *
 * Every payload is parsed against its real contract rather than cast. All three
 * of these views exist to keep an absence apart from a zero — funding nobody
 * agreed, a team nobody staffed, a saving nobody could compute — and a schema
 * that let a number through where the contract says nullable would collapse the
 * distinction the screens are built to show.
 *
 * Amounts cross this boundary as decimal strings, never numbers: a JSON number
 * is a double, and the cent it loses on a nine-figure budget is a cent finance
 * will find.
 */

const FundingEnvelope = z.object({ data: FundingView, meta: QueryMetadata });
const CostsEnvelope = z.object({ data: CostAttributionView, meta: QueryMetadata });
const CapacityEnvelope = z.object({ data: CapacityView, meta: QueryMetadata });
const CatalogueEnvelope = z.object({ data: ScenarioCatalogueView, meta: QueryMetadata });
const CompareEnvelope = z.object({ data: ScenarioComparisonView, meta: QueryMetadata });

export type WriteOutcome<T = void> =
  { ok: true; value: T } | { ok: false; message: string; problems?: string[] };

/** The server names the rule that refused; a generic failure would discard it. */
async function refusal<T>(response: Response): Promise<WriteOutcome<T>> {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    problems?: string[];
  };
  return {
    ok: false,
    message: body.message ?? `Request failed (${response.status}).`,
    problems: body.problems,
  };
}

export interface FundingDraft {
  subjectEntityId: string;
  amount: string;
  currency?: string;
  periodStart: string;
  periodEnd: string;
  basis: string;
}

export function createHttpPlanningRepository(baseUrl: string) {
  return {
    // ── Funding and forecast ────────────────────────────────────────────────
    async getFunding(
      filters: GlobalFilters,
      options: { level?: string; fundingYear?: number } = {},
    ): Promise<QueryResult<FundingView>> {
      const params = filterParams(filters);
      if (options.level) params.set('level', options.level);
      if (options.fundingYear) params.set('fundingYear', String(options.fundingYear));
      const response = await apiFetch(`${baseUrl}/funding?${params.toString()}`);
      if (!response.ok) throw new Error(`GET /funding failed: ${response.status}`);
      return FundingEnvelope.parse((await response.json()) as unknown);
    },

    async setFunding(draft: FundingDraft): Promise<WriteOutcome<string>> {
      const response = await apiFetch(`${baseUrl}/funding`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { data: { id: string } };
      return { ok: true, value: body.data.id };
    },

    async decideFunding(
      planId: string,
      approve: boolean,
      comments: string,
    ): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/funding/${planId}/approve`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ approve, comments }),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    // ── Cost attribution ────────────────────────────────────────────────────
    async getCosts(
      filters: GlobalFilters,
      options: { subjectId?: string; unallocatedOnly?: boolean } = {},
    ): Promise<QueryResult<CostAttributionView>> {
      const params = filterParams(filters);
      if (options.subjectId) params.set('subjectId', options.subjectId);
      if (options.unallocatedOnly) params.set('unallocatedOnly', 'true');
      const response = await apiFetch(`${baseUrl}/costs?${params.toString()}`);
      if (!response.ok) throw new Error(`GET /costs failed: ${response.status}`);
      return CostsEnvelope.parse((await response.json()) as unknown);
    },

    /**
     * Replace the whole split for one cost.
     *
     * The whole set, not one row: RM-006 is a deferred constraint evaluated at
     * commit, so a rebalance that passes through an over-allocated intermediate
     * is legal — and row-by-row edits would be refused for a state nobody ever
     * observes.
     */
    async reallocate(
      costId: string,
      allocations: Array<{ targetEntityId: string; share: number }>,
      rationale: string,
    ): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/costs/${costId}/allocations`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ allocations, rationale, basis: 'Manual' }),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    // ── Capacity against demand ─────────────────────────────────────────────
    async getCapacity(filters: GlobalFilters): Promise<QueryResult<CapacityView>> {
      const response = await apiFetch(`${baseUrl}/capacity?${filterParams(filters).toString()}`);
      if (!response.ok) throw new Error(`GET /capacity failed: ${response.status}`);
      return CapacityEnvelope.parse((await response.json()) as unknown);
    },

    // ── Scenarios ───────────────────────────────────────────────────────────
    async getScenarios(): Promise<QueryResult<ScenarioCatalogueView>> {
      const response = await apiFetch(`${baseUrl}/scenarios`);
      if (!response.ok) throw new Error(`GET /scenarios failed: ${response.status}`);
      return CatalogueEnvelope.parse((await response.json()) as unknown);
    },

    async compare(
      scenarioId: string,
      filters: GlobalFilters,
    ): Promise<QueryResult<ScenarioComparisonView>> {
      const response = await apiFetch(
        `${baseUrl}/scenarios/${scenarioId}/compare?${filterParams(filters).toString()}`,
      );
      if (!response.ok) throw new Error(`GET /scenarios/:id/compare failed: ${response.status}`);
      return CompareEnvelope.parse((await response.json()) as unknown);
    },

    async createScenario(name: string, subjectEntityId?: string): Promise<WriteOutcome<string>> {
      const response = await apiFetch(`${baseUrl}/scenarios`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, subjectEntityId }),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { data: { id: string } };
      return { ok: true, value: body.data.id };
    },

    async setAssumptions(
      scenarioId: string,
      assumptions: Record<string, number>,
    ): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/scenarios/${scenarioId}/assumptions`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ assumptions }),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    async recalculate(scenarioId: string): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/scenarios/${scenarioId}/recalculate`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    async decideScenario(
      scenarioId: string,
      approve: boolean,
      comments: string,
    ): Promise<WriteOutcome<void>> {
      const response = await apiFetch(`${baseUrl}/scenarios/${scenarioId}/approve`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ approve, comments }),
      });
      return response.ok ? { ok: true, value: undefined } : refusal(response);
    },

    async cloneScenario(scenarioId: string, name?: string): Promise<WriteOutcome<string>> {
      const response = await apiFetch(`${baseUrl}/scenarios/${scenarioId}/clone`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(name ? { name } : {}),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { data: { id: string } };
      return { ok: true, value: body.data.id };
    },
  };
}
